/**
 * Chatroom — proposal mockup §3 (page 4): live chat + file share with the
 * "secure session" banner, leave button, file upload area.
 *
 * Uses the WebSocket client wrapper (./ws.ts) for reconnect / heartbeat /
 * out-of-order buffering. Files go through the REST API (presigned PUT).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ulid } from 'ulid'
import { postWithAuth } from '../api'
import { connectRoomWs, type ChatFrame } from './ws'
import { loadChatSession } from './session-storage'
import { SessionCountdown } from '../components/SessionCountdown'
import { TypingIndicator } from './TypingIndicator'
import { useTypingIndicator } from './useTypingIndicator'

const ALLOWED_MIMES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]
const MAX_FILE_SIZE = 10 * 1024 * 1024

interface DisplayMessage {
  id: string
  role: 'agent' | 'customer' | 'system'
  name: string | null
  body: string
  at: number
  kind: 'msg' | 'system' | 'file'
  /** Populated when kind === 'file'. */
  file?: {
    fileId: string
    filename: string | null
    mime: string | null
    size: number | null
    uploaderRole: 'agent' | 'customer' | null
    /** 'pending' (uploading), 'ready' (downloadable), 'rejected' (quarantined). */
    state: 'pending' | 'ready' | 'rejected'
    reason?: string | null
  }
}

export function Chatroom({ role }: { role: 'agent' | 'customer' }) {
  const { id: sessionId = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [messages, setMessages] = useState<DisplayMessage[]>([])
  const [text, setText] = useState('')
  const [subscriberName, setSubscriberName] = useState<string>('')
  const [agentDisplayName, setAgentDisplayName] = useState<string | null>(null)
  /**
   * UTC ms epoch the session expires at — drives the live header
   * countdown. Read once at mount from sessionStorage (the resolve /
   * agent-join handler stashed it via saveChatSession). `null` until
   * loaded, which renders "Expired" until the load completes.
   */
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [filesPanelOpen, setFilesPanelOpen] = useState(false)
  const [preview, setPreview] = useState<{
    file: NonNullable<DisplayMessage['file']>
    url: string
  } | null>(null)
  const wsRef = useRef<ReturnType<typeof connectRoomWs> | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  /**
   * Typing-indicator wiring. The hook owns the send-debounce, the
   * receiver auto-hide, and the cleanup-on-unmount stop frame. The
   * `send` callback is stable across renders (uses `wsRef.current`)
   * so the hook's internal timers don't churn on every render.
   */
  const sendTyping = useCallback(
    (frame: { t: 'typing'; state: 'start' | 'stop' }): void => {
      wsRef.current?.send(frame)
    },
    [],
  )
  const typing = useTypingIndicator(sendTyping)

  // Files in this session, derived from the message stream so the panel
  // and the inline cards stay in sync. Only "ready" files are downloadable.
  const sessionFiles = messages
    .filter((m) => m.kind === 'file' && m.file)
    .map((m) => m.file!)
  const fileCount = sessionFiles.filter((f) => f.state === 'ready').length

  useEffect(() => {
    // Load all per-session state via the centralised helper (DRY).
    // Returns null if any required key is missing — typically means the
    // user navigated to /room/:id directly without going through the
    // resolve / agent-join handshake. The WS layer will reject the
    // upgrade and the user lands back on the join page.
    const state = loadChatSession(sessionId)
    if (!state) return
    setSubscriberName(state.subscriberName)
    setAgentDisplayName(state.agentDisplayName || null)
    setSessionExpiresAt(state.expiresAt)
  }, [sessionId])

  useEffect(() => {
    let token: string | null = null
    if (role === 'customer') {
      token = sessionStorage.getItem(`ws:${sessionId}`)
      if (!token) { navigate('/join'); return }
    } else {
      // Agent path issues a wsToken from the server-side join helper —
      // not yet implemented; using the session id as a placeholder for
      // the dev demo. The DO will reject without a valid token.
      token = sessionStorage.getItem(`ws:${sessionId}`) ?? ''
    }

    const conn = connectRoomWs(sessionId, token, {
      onFrame: (f) => handleFrame(f),
      onOpen: () => setError(null),
      onClose: () => { /* surfaced via room.expired etc */ },
      onError: () => setError('Connection lost. Retrying…'),
      onGiveUp: (reason) => {
        setError(null)
        setExpired(true)
        // Persist the give-up reason as a system message so the user
        // sees a clean "Session ended" line in the transcript instead
        // of an infinite "retrying" spinner.
        setMessages((m) => [
          ...m,
          {
            id: ulid(),
            role: 'system',
            name: null,
            body:
              reason === 'expired'
                ? 'This session has ended.'
                : 'Connection could not be re-established.',
            at: Date.now(),
            kind: 'system',
          },
        ])
      },
    })
    wsRef.current = conn
    return () => conn.close()
  }, [sessionId, role, navigate])

  function handleFrame(f: ChatFrame) {
    switch (f.t) {
      case 'msg':
        setMessages((m) => dedup([...m, {
          id: f.id, role: f.role, name: f.name ?? null, body: f.body, at: f.at, kind: 'msg',
        }]))
        // Defensive: an incoming msg from the OTHER side implies they
        // stopped typing, even if the explicit stop frame was lost.
        if (f.role !== role) typing.onRemoteActivity()
        break
      case 'typing':
        // Only react to frames from the OTHER party. Server doesn't
        // echo to sender, but defensive guard makes the contract
        // explicit at the consumer layer too.
        if (f.role !== role) {
          typing.onRemoteFrame(f.state, f.role, f.name ?? null)
        }
        break
      case 'system':
        setMessages((m) => [...m, {
          id: ulid(),
          role: 'system',
          name: null,
          body: f.kind === 'participant_joined'
            ? `${cap(f.role ?? 'someone')} joined`
            : `${cap(f.role ?? 'someone')} left`,
          at: Date.now(),
          kind: 'system',
        }])
        break
      case 'policy.violation':
        setError(`Message blocked: ${f.reason}`)
        break
      case 'file.ready':
        // Replace any pending "file:<id>" entry (placed when this side
        // uploaded), or append a new one (other side uploaded).
        setMessages((m) => upsertFile(m, {
          id: `file:${f.id}`,
          role: 'system',
          name: null,
          body: '',
          at: Date.now(),
          kind: 'file',
          file: {
            fileId: f.id,
            filename: f.filename ?? null,
            mime: f.mime ?? null,
            size: f.size ?? null,
            uploaderRole: f.uploaderRole ?? null,
            state: 'ready',
          },
        }))
        break
      case 'file.rejected':
        setMessages((m) => upsertFile(m, {
          id: `file:${f.id}`,
          role: 'system',
          name: null,
          body: '',
          at: Date.now(),
          kind: 'file',
          file: {
            fileId: f.id,
            filename: f.filename ?? null,
            mime: null,
            size: null,
            uploaderRole: null,
            state: 'rejected',
            reason: f.reason,
          },
        }))
        break
      case 'room.expired':
        setExpired(true)
        break
    }
  }

  function send(e: React.FormEvent) {
    e.preventDefault()
    if (!text.trim() || expired) return
    const id = ulid()
    wsRef.current?.send({ t: 'msg', id, body: text })
    setText('')
    // Sending a message implies "done typing" — emit stop immediately
    // rather than waiting for the 4s idle timer. Prevents the brief
    // "X is typing" flash that would otherwise overlap with the
    // newly-arrived message bubble.
    typing.forceStop()
  }

  async function onFilePicked(file: File) {
    setError(null)
    if (!ALLOWED_MIMES.includes(file.type)) {
      setError('File type not allowed.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File exceeds the 10 MB cap.')
      return
    }
    const sha256 = await sha256Hex(file)
    try {
      // Both roles authenticate file ops with the room-scoped wsToken so
      // the same code path works regardless of which cookies happen to
      // be set in the browser (in dev one origin holds both cookies).
      const wsToken = sessionStorage.getItem(`ws:${sessionId}`)
      const authHeader: Record<string, string> =
        wsToken ? { authorization: `Bearer ${wsToken}` } : {}

      const init = await postWithAuth<{ fileId: string; presignedPutUrl: string }>(
        '/api/files/initiate',
        { sessionId, filename: file.name, mime: file.type, size: file.size, sha256 },
        authHeader,
      )

      // Optimistic file card so the uploader sees their own action
      // immediately. Keyed by `file:<id>` so the file.ready frame can
      // upgrade it in place via upsertFile().
      setMessages((m) => upsertFile(m, {
        id: `file:${init.fileId}`,
        role: 'system',
        name: null,
        body: '',
        at: Date.now(),
        kind: 'file',
        file: {
          fileId: init.fileId,
          filename: file.name,
          mime: file.type,
          size: file.size,
          uploaderRole: role,
          state: 'pending',
        },
      }))

      // Direct PUT to R2 (or to the dev local-r2 proxy).
      const put = await fetch(init.presignedPutUrl, {
        method: 'PUT',
        headers: { 'content-type': file.type, 'x-amz-content-sha256': sha256 },
        body: file,
      })
      if (!put.ok) throw new Error(`PUT ${put.status}`)
      await postWithAuth(`/api/files/${init.fileId}/confirm`, {}, authHeader)
    } catch (e) {
      setError(`Upload failed: ${e instanceof Error ? e.message : 'unknown'}`)
    }
  }

  /**
   * View a file in an in-page modal — never opens a new tab. The
   * presigned URL is consumed inside the SPA so it doesn't leak into
   * browser history or any user-visible URL bar. The modal component
   * decides per-mime whether to render inline (img / iframe-pdf) or
   * surface a confirmed download (docx).
   */
  async function onView(file: NonNullable<DisplayMessage['file']>) {
    setError(null)
    try {
      const wsToken = sessionStorage.getItem(`ws:${sessionId}`)
      const headers: Record<string, string> =
        wsToken ? { authorization: `Bearer ${wsToken}` } : {}
      const res = await fetch(`/api/files/${file.fileId}/download`, {
        credentials: 'include',
        headers,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const { url } = (await res.json()) as { url: string }
      setPreview({ file, url })
    } catch (e) {
      setError(`Could not load file: ${e instanceof Error ? e.message : 'unknown'}`)
    }
  }

  function leave() {
    wsRef.current?.send({ t: 'leave' })
    wsRef.current?.close()
    navigate(role === 'agent' ? '/agent/console' : '/')
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/*
        Header: collapses to two-row layout on mobile (logo+room on top,
        leave button below); flex-wrap keeps everything visible on phones.
      */}
      <header className="bg-white sm:rounded-t-2xl shadow-sm border border-b-0 border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
          <span className="inline-block w-6 h-6 rounded bg-blue-600 shrink-0" aria-hidden />
          <span className="font-semibold">SecureBridge</span>
          <span className="text-xs text-slate-500 truncate">
            Room: {sessionId.slice(-8).toUpperCase()}
          </span>
          <span className="text-xs text-slate-500 hidden sm:inline">Policy: Standard</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          {/*
            Live session countdown. Both parties see the same wall-
            clock so the conversation pace matches the budget. The
            colour shifts to amber under 5 min, rose under 1 min — a
            visual nudge to wrap up before the room expires. When
            sessionExpiresAt is null (cold-load before sessionStorage
            read), the component renders "Expired" — the WS layer's
            normal recovery flow handles the navigation.
          */}
          <SessionCountdown expiresAt={sessionExpiresAt} size="sm" />
          <button
            onClick={() => setFilesPanelOpen(true)}
            className="rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium px-3 py-1.5 hover:bg-slate-50"
          >
            Files{fileCount > 0 ? ` (${fileCount})` : ''}
          </button>
          <button
            onClick={leave}
            className="rounded-lg border border-slate-300 bg-white text-slate-700 text-sm font-medium px-3 py-1.5 hover:bg-slate-50"
          >
            Leave
          </button>
        </div>
      </header>

      {subscriberName && (
        <div className="bg-blue-50 border-x border-blue-100 px-4 sm:px-6 py-2 text-sm text-blue-900">
          You're chatting with <strong>{subscriberName}</strong>
          {agentDisplayName && ` · agent ${agentDisplayName}`}
        </div>
      )}

      <div className="bg-slate-50 border-x border-slate-200 px-4 sm:px-6 py-2 text-xs text-slate-600">
        This is a secure session. Do not share sensitive info unless requested by your company.
      </div>

      <div className="bg-white border-x border-slate-200 px-4 sm:px-6 py-4 min-h-[300px] max-h-[55vh] overflow-y-auto space-y-3">
        {messages.map((m) =>
          m.kind === 'file' && m.file ? (
            <FileBubble
              key={m.id}
              file={m.file}
              self={m.file.uploaderRole === role}
              at={m.at}
              onView={onView}
            />
          ) : (
            <Bubble key={m.id} m={m} self={m.role === role} />
          ),
        )}
      </div>

      {/* File upload + composer */}
      <div className="bg-blue-50 border-x border-blue-100 p-3 sm:p-4">
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            const f = e.dataTransfer.files[0]
            if (f) void onFilePicked(f)
          }}
          className="text-center text-sm text-blue-900 border-2 border-dashed border-blue-200 rounded-lg p-3 sm:p-4"
        >
          <div className="font-medium">Drop files here or click to upload</div>
          <div className="text-xs mt-1">PDF, PNG, JPG, DOCX · Max 10 MB</div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-2 rounded-md bg-blue-600 text-white text-xs px-3 py-1 hover:bg-blue-700"
          >
            Choose File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.docx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) void onFilePicked(f)
            }}
          />
        </div>
      </div>

      {/*
        Typing indicator strip — fixed-height container above the
        composer (no layout shift when it appears/disappears). The
        hook handles all timing; this component is purely visual.
      */}
      <div className="bg-white border-x border-slate-200">
        <TypingIndicator remote={typing.remote} />
      </div>

      <form
        onSubmit={send}
        className="bg-white sm:rounded-b-2xl border border-t-0 border-slate-200 px-3 sm:px-4 py-3 flex items-center gap-2"
      >
        <input
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            // Notify the typing hook of every keystroke. The hook
            // handles debouncing internally (one start frame per
            // burst, auto-stop after 4s of idle).
            typing.onLocalInputChange(e.target.value)
          }}
          onBlur={() => typing.forceStop()}
          placeholder="Type a message..."
          disabled={expired}
          className="flex-1 min-w-0 rounded-lg border border-slate-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoComplete="off"
        />
        <button
          type="submit"
          disabled={expired || !text.trim()}
          className="rounded-lg bg-blue-600 text-white text-sm font-medium px-4 py-2 hover:bg-blue-700 disabled:opacity-50 shrink-0"
        >
          Send
        </button>
      </form>

      {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
      {expired && (
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          This session has ended.
        </div>
      )}

      <p className="mt-4 text-xs text-slate-500 text-center">
        Session may be logged for compliance.
      </p>

      <FilesPanel
        open={filesPanelOpen}
        onClose={() => setFilesPanelOpen(false)}
        files={sessionFiles}
        selfRole={role}
        onView={onView}
      />

      {preview && (
        <FilePreviewModal
          file={preview.file}
          url={preview.url}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  )
}

function Bubble({ m, self }: { m: DisplayMessage; self: boolean }) {
  if (m.kind === 'system') {
    return (
      <div className="text-center text-xs text-slate-500 italic">{m.body}</div>
    )
  }
  return (
    <div className={`flex ${self ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
        self ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-900'
      }`}>
        {m.name && (
          <div className={`text-xs font-medium mb-0.5 ${self ? 'text-blue-100' : 'text-slate-500'}`}>
            {m.name}
          </div>
        )}
        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
        <div className={`text-[10px] mt-1 ${self ? 'text-blue-200' : 'text-slate-400'}`}>
          {new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  )
}

/**
 * In-place upsert of a file message: when the file.ready/file.rejected
 * frame for an upload that we already have a "pending" optimistic card
 * for arrives, replace the existing card; otherwise append.
 */
function upsertFile(messages: DisplayMessage[], next: DisplayMessage): DisplayMessage[] {
  const idx = messages.findIndex((m) => m.id === next.id)
  if (idx === -1) return [...messages, next]
  const out = messages.slice()
  out[idx] = { ...messages[idx]!, ...next }
  return out
}

function FileBubble({
  file,
  self,
  at,
  onView,
}: {
  file: NonNullable<DisplayMessage['file']>
  self: boolean
  at: number
  onView: (file: NonNullable<DisplayMessage['file']>) => void
}) {
  const viewable = file.state === 'ready'
  const tail = file.fileId.slice(-4).toUpperCase()
  return (
    <div className={`flex ${self ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 border ${
          self
            ? 'bg-blue-50 border-blue-200 text-blue-950'
            : 'bg-white border-slate-200 text-slate-900'
        }`}
      >
        <div className="flex items-start gap-3">
          <div className="text-2xl shrink-0" aria-hidden>
            {mimeIcon(file.mime)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate" title={file.filename ?? file.fileId}>
              {file.filename ?? `file-${tail}`}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              {file.size != null && <span>{formatBytes(file.size)}</span>}
              <span title={file.fileId}>id: {tail}</span>
              {file.uploaderRole && (
                <span>· {self ? 'You' : capRole(file.uploaderRole)}</span>
              )}
            </div>
            {file.state === 'pending' && (
              <div className="mt-2 text-xs text-amber-700">Scanning…</div>
            )}
            {file.state === 'rejected' && (
              <div className="mt-2 text-xs text-rose-700">
                Quarantined — {file.reason ?? 'policy violation'}
              </div>
            )}
            {viewable && (
              <button
                onClick={() => onView(file)}
                className="mt-2 text-xs font-medium text-blue-700 hover:text-blue-900 underline"
              >
                View
              </button>
            )}
          </div>
        </div>
        <div className={`text-[10px] mt-2 ${self ? 'text-blue-700' : 'text-slate-400'}`}>
          {new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  )
}

function FilesPanel({
  open,
  onClose,
  files,
  selfRole,
  onView,
}: {
  open: boolean
  onClose: () => void
  files: NonNullable<DisplayMessage['file']>[]
  selfRole: 'agent' | 'customer'
  onView: (file: NonNullable<DisplayMessage['file']>) => void
}) {
  if (!open) return null
  // Files sorted newest-first to match how an agent thinks ("show me what
  // they just uploaded").
  const sorted = [...files].reverse()
  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      role="dialog"
      aria-label="Session files"
    >
      <button
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30"
        aria-label="Close files panel"
      />
      <div className="relative w-full max-w-md bg-white shadow-xl border-l border-slate-200 flex flex-col">
        <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Session Files</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {sorted.length} file{sorted.length === 1 ? '' : 's'} · auto-deletes 24h after session ends
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 text-xl leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
          {sorted.length === 0 && (
            <p className="text-sm text-slate-500 italic px-2 py-6 text-center">
              No files in this session yet.
            </p>
          )}
          {sorted.map((f) => {
            const viewable = f.state === 'ready'
            const tail = f.fileId.slice(-4).toUpperCase()
            const self = f.uploaderRole === selfRole
            return (
              <div
                key={f.fileId}
                className="border border-slate-200 rounded-lg p-3 flex items-start gap-3"
              >
                <div className="text-2xl shrink-0" aria-hidden>
                  {mimeIcon(f.mime)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" title={f.filename ?? f.fileId}>
                    {f.filename ?? `file-${tail}`}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
                    {f.size != null && <span>{formatBytes(f.size)}</span>}
                    <span>id: {tail}</span>
                    {f.uploaderRole && (
                      <span>{self ? 'You uploaded' : `${capRole(f.uploaderRole)} uploaded`}</span>
                    )}
                  </div>
                  {f.state === 'pending' && (
                    <div className="text-xs text-amber-700 mt-1">Scanning…</div>
                  )}
                  {f.state === 'rejected' && (
                    <div className="text-xs text-rose-700 mt-1">
                      Quarantined — {f.reason ?? 'policy violation'}
                    </div>
                  )}
                </div>
                {viewable && (
                  <button
                    onClick={() => onView(f)}
                    className="shrink-0 text-xs font-medium text-blue-700 hover:text-blue-900 border border-blue-200 rounded px-2 py-1 hover:bg-blue-50"
                  >
                    View
                  </button>
                )}
              </div>
            )
          })}
        </div>
        <footer className="px-5 py-3 border-t border-slate-200 text-xs text-slate-500">
          Files preview in-page only. Each "View" issues a fresh 5-min
          presigned link, audited per access. Files auto-delete 24h
          after the session ends.
        </footer>
      </div>
    </div>
  )
}

/**
 * In-page preview modal — files are NEVER opened in a new tab. The
 * presigned URL is consumed in an iframe / img element rendered inside
 * the SPA, so:
 *   - the URL doesn't appear in browser history or the URL bar
 *   - the agent stays in the chat surface (call-center context)
 *   - we can overlay watermarks / "view-only" banners
 *   - closing the modal removes the file from the visible DOM
 *
 * For DOCX (and any mime browsers can't render natively) we present an
 * explicit "Download anyway" path with a confirmation step — this is
 * the only path where the file can leave the session as a local copy.
 */
function FilePreviewModal({
  file,
  url,
  onClose,
}: {
  file: NonNullable<DisplayMessage['file']>
  url: string
  onClose: () => void
}) {
  // Browsers handle PDF + image inline. DOCX cannot be rendered.
  const mime = file.mime ?? ''
  const previewable =
    mime.startsWith('image/') || mime === 'application/pdf'

  // Allow Esc to close.
  const escClose = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview ${file.filename ?? 'file'}`}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onKeyDown={escClose}
    >
      <button
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm"
        aria-label="Close preview"
      />
      <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col bg-white rounded-xl shadow-2xl overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate" title={file.filename ?? file.fileId}>
              {mimeIcon(file.mime)} {file.filename ?? `file-${file.fileId.slice(-4).toUpperCase()}`}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-2">
              {file.size != null && <span>{formatBytes(file.size)}</span>}
              <span>{file.mime ?? 'unknown type'}</span>
              <span>id: {file.fileId.slice(-4).toUpperCase()}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 text-2xl leading-none px-2 shrink-0"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="flex-1 min-h-0 bg-slate-50 flex items-center justify-center overflow-hidden">
          {mime.startsWith('image/') && (
            <img
              src={url}
              alt={file.filename ?? 'file preview'}
              className="max-w-full max-h-full object-contain"
              draggable={false}
            />
          )}
          {mime === 'application/pdf' && (
            <iframe
              title={file.filename ?? 'PDF preview'}
              src={url}
              // sandbox="allow-scripts" lets the embedded PDF viewer work
              // in Chromium without granting access to our origin's cookies
              // or storage. allow-same-origin is intentionally OMITTED.
              sandbox="allow-scripts"
              className="w-full h-full border-0"
            />
          )}
          {!previewable && (
            <DownloadFallback file={file} url={url} />
          )}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between gap-3">
          <span>
            View-only · do not screenshot or save · this preview link
            expires in 5 minutes · this access is audited
          </span>
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white text-slate-700 text-xs font-medium px-3 py-1.5 hover:bg-slate-50 shrink-0"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  )
}

function DownloadFallback({
  file,
  url,
}: {
  file: NonNullable<DisplayMessage['file']>
  url: string
}) {
  const [confirmed, setConfirmed] = useState(false)
  // Single-fire guard: React 19 concurrent mode replays renders, and any
  // parent re-render (e.g. a WS frame arriving while the modal is open)
  // would otherwise re-trigger the download. Using a ref keeps the guard
  // outside React's reconciliation. (Fix for v0.3 audit §1.10.)
  const downloadFiredRef = useRef(false)

  // The download is a side effect — it does NOT belong in the render
  // path. We trigger it from an effect that runs once when `confirmed`
  // flips true, and only once for the lifetime of this component
  // instance. If the user wants to re-download, they reopen the modal.
  useEffect(() => {
    if (!confirmed || downloadFiredRef.current) return
    downloadFiredRef.current = true
    const a = document.createElement('a')
    a.href = url
    a.download = file.filename ?? `file-${file.fileId.slice(-4)}`
    a.rel = 'noopener'
    a.click()
  }, [confirmed, url, file.filename, file.fileId])

  if (!confirmed) {
    return (
      <div className="max-w-md p-6 text-center text-sm text-slate-700 space-y-3">
        <p>
          <strong>{file.filename ?? 'This file'}</strong> can't be previewed in
          the browser.
        </p>
        <p className="text-xs text-slate-500">
          Downloading puts a copy of the file on your local machine. The
          chat session expires automatically; the file you save here does
          not.
        </p>
        <button
          onClick={() => setConfirmed(true)}
          className="rounded-lg bg-rose-600 text-white text-sm font-medium px-4 py-2 hover:bg-rose-700"
        >
          I understand — download anyway
        </button>
      </div>
    )
  }
  return (
    <div className="text-sm text-slate-700">
      Download started. You may close this window.
    </div>
  )
}

function mimeIcon(mime: string | null | undefined): string {
  if (!mime) return '📄'
  if (mime.startsWith('image/')) return '🖼️'
  if (mime === 'application/pdf') return '📕'
  if (mime.includes('word')) return '📘'
  return '📄'
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

function capRole(role: 'agent' | 'customer'): string {
  return role === 'agent' ? 'Agent' : 'Customer'
}

function dedup(messages: DisplayMessage[]): DisplayMessage[] {
  const seen = new Set<string>()
  const out: DisplayMessage[] = []
  for (const m of messages) {
    if (seen.has(m.id)) continue
    seen.add(m.id)
    out.push(m)
  }
  return out
}

function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1) }

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buf)
  const arr = new Uint8Array(digest)
  let hex = ''
  for (let i = 0; i < arr.length; i++) hex += (arr[i] ?? 0).toString(16).padStart(2, '0')
  return hex
}
